import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Building2, Package, Calendar, ExternalLink, MessageSquare, Edit2, Check, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * FollowingProductsTable - Tableau des produits suivis (À suivre)
 * 
 * Colonnes:
 * 1. Visuel (miniature)
 * 2. Désignation (lien vers fiche produit)
 * 3. Distributeur (partenaire)
 * 4. Commentaire (éditable inline — même UX que Favoris)
 * 5. Dates précommande (début → fin)
 * 6. Date de sortie
 */
const FollowingProductsTable = ({ products = [], loading = false, onCommentUpdate = null }) => {
  
  const [editingId, setEditingId] = useState(null);
  const [editingComment, setEditingComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  
  // Formater une date en DD/MM/YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      if (isNaN(date)) return '—';
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return '—';
    }
  };

  // Formater les dates de précommande (depuis distributions.promotional_mechanics)
  const formatPreorderDates = (product) => {
    const dists = product.distributions || [];
    const precoMech = dists.flatMap(d => (d.promotional_mechanics || []))
      .find(m => m.nature_offre === 'PRECOMMANDE');
    const start = formatDate(precoMech?.date_debut);
    const end = formatDate(precoMech?.date_fin);
    
    if (start === '—' && end === '—') return '—';
    if (start === '—') return `→ ${end}`;
    if (end === '—') return `${start} →`;
    return `${start} → ${end}`;
  };

  // Obtenir l'image du produit
  const getProductImage = (product) => {
    if (product.visuels && product.visuels.length > 0) {
      return product.visuels[0];
    }
    if (product.images && product.images.length > 0) {
      return product.images[0];
    }
    return null;
  };

  // Démarrer l'édition d'un commentaire
  const startEditing = useCallback((fav) => {
    setEditingId(fav.id);
    setEditingComment(fav.commentaire || '');
  }, []);

  // Annuler l'édition
  const cancelEditing = useCallback(() => {
    setEditingId(null);
    setEditingComment('');
  }, []);

  // Sauvegarder le commentaire via product_comments (source de vérité V2)
  const saveComment = useCallback(async (favoriteId) => {
    setSavingComment(true);
    try {
      // Trouver le product_id du record suivi
      const fav = products.find(f => f.id === favoriteId);
      const productId = fav?.product_id;
      if (!productId) throw new Error('Product ID not found');
      
      await api.put('/product-comments', {
        product_id: productId,
        comment: editingComment.trim() || null
      });
      
      toast.success('Commentaire mis à jour');
      
      if (onCommentUpdate) {
        onCommentUpdate(favoriteId, editingComment.trim() || null);
      }
      
      setEditingId(null);
      setEditingComment('');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingComment(false);
    }
  }, [editingComment, onCommentUpdate, products]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Desktop/Tablet Table View */}
      <div className="hidden sm:block overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-[80px]">Visuel</TableHead>
              <TableHead className="min-w-[180px]">Désignation</TableHead>
              <TableHead className="min-w-[130px]">Distributeur</TableHead>
              <TableHead className="min-w-[200px]">Commentaire</TableHead>
              <TableHead className="min-w-[160px]">Précommande</TableHead>
              <TableHead className="min-w-[100px]">Sortie</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Aucun produit dans votre liste de suivi</p>
                </TableCell>
              </TableRow>
            ) : (
              products.map((fav) => {
                const product = fav.product;
                if (!product) return null;
                
                const imageUrl = getProductImage(product);
                const isEditing = editingId === fav.id;
                
                return (
                  <TableRow 
                    key={fav.id || product.id} 
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`following-row-${product.id}`}
                  >
                    {/* Visuel */}
                    <TableCell className="p-2">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={product.intitule}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-full h-full items-center justify-center ${imageUrl ? 'hidden' : 'flex'}`}
                        >
                          <Package className="h-6 w-6 text-gray-400" />
                        </div>
                      </div>
                    </TableCell>
                    
                    {/* Désignation */}
                    <TableCell>
                      <Link 
                        to={`/dashboard/produit/${product.id}`}
                        className="group flex flex-col gap-1"
                      >
                        <span className="font-medium text-gray-900 group-hover:text-primary transition-colors line-clamp-2">
                          {product.intitule || 'Sans nom'}
                        </span>
                        {product.publishers?.[0]?.name && (
                          <span className="text-xs text-muted-foreground">
                            {product.publishers.map(p => p.name).filter(Boolean).join(' / ') || ''}
                          </span>
                        )}
                      </Link>
                    </TableCell>
                    
                    {/* Distributeur */}
                    <TableCell>
                      {product.partner_name ? (
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          <span className="text-sm">{product.partner_name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    
                    {/* Commentaire — édition inline */}
                    <TableCell>
                      {isEditing ? (
                        <div className="flex flex-col gap-2">
                          <Textarea
                            value={editingComment}
                            onChange={(e) => setEditingComment(e.target.value)}
                            placeholder="Votre commentaire personnel..."
                            className="min-h-[60px] text-sm resize-none"
                            maxLength={500}
                            autoFocus
                            data-testid={`following-comment-textarea-${fav.id}`}
                          />
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={() => saveComment(fav.id)}
                              disabled={savingComment}
                              data-testid={`following-save-comment-${fav.id}`}
                            >
                              <Check className="h-3 w-3 mr-1" />
                              {savingComment ? '...' : 'OK'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={cancelEditing}
                              disabled={savingComment}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            <span className="text-xs text-muted-foreground ml-auto">
                              {editingComment.length}/500
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="group cursor-pointer p-2 -m-2 rounded hover:bg-gray-100 transition-colors min-h-[40px] flex items-start gap-2"
                          onClick={() => startEditing(fav)}
                          data-testid={`following-comment-display-${fav.id}`}
                        >
                          {fav.commentaire ? (
                            <p className="text-sm text-gray-700 line-clamp-3 flex-1">
                              {fav.commentaire}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground italic flex-1">
                              Ajouter un commentaire...
                            </p>
                          )}
                          <Edit2 className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1" />
                        </div>
                      )}
                    </TableCell>
                    
                    {/* Dates précommande */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <span className="text-sm whitespace-nowrap">
                          {formatPreorderDates(product)}
                        </span>
                      </div>
                    </TableCell>
                    
                    {/* Date de sortie */}
                    <TableCell>
                      <span className="text-sm">
                        {formatDate(product.date_sortie_iso)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden divide-y">
        {products.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Aucun produit dans votre liste de suivi</p>
          </div>
        ) : (
          products.map((fav) => {
            const product = fav.product;
            if (!product) return null;
            
            const imageUrl = getProductImage(product);
            const isEditing = editingId === fav.id;
            
            return (
              <div 
                key={fav.id || product.id}
                className="p-4 hover:bg-gray-50 transition-colors"
                data-testid={`following-card-mobile-${product.id}`}
              >
                <div className="flex gap-4">
                  {/* Visuel */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={product.intitule}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Package className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <Link 
                      to={`/dashboard/produit/${product.id}`}
                      className="font-medium text-gray-900 hover:text-primary transition-colors line-clamp-2 mb-1 block"
                    >
                      {product.intitule || 'Sans nom'}
                    </Link>
                    
                    {/* Distributeur */}
                    {product.partner_name && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                        <Building2 className="h-3 w-3 text-blue-500" />
                        {product.partner_name}
                      </div>
                    )}
                    
                    {/* Dates en badges */}
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="text-xs">
                        <Calendar className="h-3 w-3 mr-1 text-orange-500" />
                        {formatPreorderDates(product)}
                      </Badge>
                      {product.date_sortie_iso && (
                        <Badge variant="secondary" className="text-xs">
                          Sortie: {formatDate(product.date_sortie_iso)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Commentaire — Mobile */}
                <div className="mt-3 pt-3 border-t">
                  {isEditing ? (
                    <div className="flex flex-col gap-2">
                      <Textarea
                        value={editingComment}
                        onChange={(e) => setEditingComment(e.target.value)}
                        placeholder="Votre commentaire personnel..."
                        className="min-h-[60px] text-sm resize-none"
                        maxLength={500}
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => saveComment(fav.id)}
                          disabled={savingComment}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          {savingComment ? '...' : 'Enregistrer'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={cancelEditing}
                          disabled={savingComment}
                        >
                          Annuler
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="flex items-start gap-2 cursor-pointer p-2 -m-2 rounded hover:bg-gray-100"
                      onClick={() => startEditing(fav)}
                      data-testid={`following-comment-mobile-${fav.id}`}
                    >
                      <MessageSquare className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      {fav.commentaire ? (
                        <p className="text-sm text-gray-700 flex-1">
                          {fav.commentaire}
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground italic flex-1">
                          Ajouter un commentaire...
                        </p>
                      )}
                      <Edit2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FollowingProductsTable;
